import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { LocalTellescopeFormResponseMap } from "./use-tellescope-form-core";
import type { FormShallowStoreHook, SubmitPhase } from "../../store";

export interface UseSubmitPhaseArgs {
  uploadPendingFiles: () => Promise<LocalTellescopeFormResponseMap>;
  useFormShallowStore: FormShallowStoreHook;
}

export const useSubmitPhase = ({ uploadPendingFiles, useFormShallowStore }: UseSubmitPhaseArgs) => {
  const { t } = useTranslation("forms");

  const { submitPhase, setSubmitPhase, setUploadProgress } = useFormShallowStore(
    "submitPhase",
    "setSubmitPhase",
    "setUploadProgress"
  );
  const submitPhaseRef = useRef<SubmitPhase>("idle");

  const runSubmit = useCallback(
    async <T>(submit: (uploadFirst: () => Promise<LocalTellescopeFormResponseMap>) => Promise<T>): Promise<T> => {
      // The ref, not the state, is the double-submit guard — state lags a render, so a second
      // call arriving in the same tick would read "idle" and slip through.
      if (submitPhaseRef.current !== "idle") throw new Error(t("A submission is already in progress"));

      submitPhaseRef.current = "uploading";
      setSubmitPhase("uploading");
      try {
        return await submit(async () => {
          const responses = await uploadPendingFiles();
          submitPhaseRef.current = "submitting";
          setSubmitPhase("submitting");
          return responses;
        });
      } finally {
        submitPhaseRef.current = "idle";
        setSubmitPhase("idle");
        setUploadProgress({ completed: 0, total: 0 });
      }
    },
    [t, uploadPendingFiles, setSubmitPhase, setUploadProgress]
  );

  return { submitPhase, runSubmit };
};
